import { useState } from 'react';
import { supabase } from '../lib/supabase';

// "Let them ask one more thing 👀" — a toggle on the recipient's completed
// screen, off by default. The recipient has to turn it on themselves; we
// never nudge or pre-select it. Flipping it on just writes
// follow_up_enabled = true on the test row and fires a free (non-premium)
// notification to the sender — replying to it is what's gated behind
// Premium, not seeing that the recipient said yes.
export default function FollowUpToggle({
  testId,
  initialEnabled,
}: {
  testId: string;
  initialEnabled: boolean;
}) {
  const [enabled, setEnabled] = useState(initialEnabled);
  const [busy, setBusy] = useState(false);

  const toggle = async () => {
    if (busy) return;
    const next = !enabled;
    setEnabled(next); // optimistic — this is a low-stakes toggle
    setBusy(true);
    try {
      const { error } = await supabase
        .from('tests')
        .update({ follow_up_enabled: next })
        .eq('id', testId);
      if (error) throw error;

      if (next) {
        // Fire-and-forget, same pattern as test_opened/test_answered — free
        // for every sender regardless of premium status. Only the sender's
        // ability to actually send the follow-up question is gated.
        // (notify-creator is a dedicated "they answered" email and isn't
        // reused here — it would send the wrong message for this event.)
        supabase.functions
          .invoke('send-push', { body: { type: 'follow_up_opted_in', testId } })
          .catch(() => {});
      }
    } catch (e) {
      console.error('FollowUpToggle update failed:', e);
      setEnabled(!next); // revert on failure
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={busy}
      aria-pressed={enabled}
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
        width: '100%', padding: '14px 18px', borderRadius: 18, cursor: busy ? 'default' : 'pointer',
        fontFamily: 'inherit', textAlign: 'left', border: '1.5px solid #fbcfe0',
        background: '#fff0f4', opacity: busy ? 0.7 : 1,
      }}
    >
      <span style={{ fontSize: 14.5, fontWeight: 800, color: '#18181b', lineHeight: 1.35 }}>
        Let them ask one more thing 👀
      </span>
      <span
        style={{
          flexShrink: 0, width: 46, height: 27, borderRadius: 999, position: 'relative',
          background: enabled ? '#e11d48' : '#e5c3cd', transition: 'background 0.2s ease',
        }}
      >
        <span
          style={{
            position: 'absolute', top: 3, left: enabled ? 22 : 3, width: 21, height: 21,
            borderRadius: '50%', background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
            transition: 'left 0.2s ease',
          }}
        />
      </span>
    </button>
  );
}
